import "../../assets/styles.css";
import "../../assets/css/plugins/animation.css";
import "../../assets/css/plugins/feature.css";
import "../../assets/css/plugins/lightbox.css";
import "../../assets/css/plugins/magnify.min.css";
import "../../assets/css/plugins/slick-theme.css";
import "../../assets/css/plugins/slick.css";
import "../../App.css";
import "react-spring-bottom-sheet/dist/style.css";
import operatorPreview from "../../assets/images/footer1.png";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Parallax,
  ParallaxProvider
} from "react-scroll-parallax";
import { BottomSheet } from "react-spring-bottom-sheet";
import { useWindowWidth } from "@react-hook/window-size";
import SalesInfrastructureField from "../../Components/ParticlesBanner/Index.jsx";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Bot,
  BrainCircuit,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileCheck2,
  Fingerprint,
  Gamepad2,
  Globe2,
  Images,
  Layers3,
  LockKeyhole,
  Maximize2,
  MonitorUp,
  Network,
  Scale,
  SearchCheck,
  ServerCog,
  ShieldCheck,
  Sparkles,
  WalletCards,
  X,
  Zap
} from "lucide-react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform
} from "framer-motion";
import SalesFooter from "../../Components/Universal/MainFooter/Index.js";
import EthosinoCapabilities from "../../Components/Homepage/Capabilities/Index.js";
import EthosinoArchitectureSection from "../../Components/Homepage/Architecture/Index.js";
import EthosinoDemoSection from "../../Components/Homepage/Demos/Demo.js";
import HeaderToMainPage from "../../Components/Universal/Header/Index.js";
import "./Styles.css";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import EthosinoTypewriter from "../../Helpers/Typewriter/EthosinoTypewriter.jsx";

const R2_BASE =
  "https://pub-93a1c3f08d9142d8b81cbf054b4cbe0d.r2.dev";

const r2Image = filename => `${R2_BASE}/${filename}`;

const playerImages = [
  // TABLE GAMES & IMMERSIVE EXPERIENCES
  "bj-fin.png",
  "bacccomputer.png",
  "baccimmersive.png",
  "crapscraps.png",
  "roulroulredone.png",
  "warwarwar.png",
  "tcp-fin.png",
  "ultimateth.png",

  // ETHOSINO ORIGINALS
  "minesregen.png",
  "plinkoregen.png",
  "bingoregen.png",
  "caseopeningregen.png",
  "thinkfastregen.png",
  "pricediscoveryregen.png",
  "scratchersregen.png",
  "pacmanregen.png",
  "patternremember.png",
  "kenoregen.png",
  "dicerangeregen.png",
  "crashregen.png",

  // UNIQUE & ARCADE-STYLE GAMES
  "basketballregen.png",
  "clawmachineregen.png",
  "gravitywellregen.png",
  "laservaultregen.png",
  "magnetdropregen.png",
  "navaldice.png",
  "strikecircuit.png",

  // SLOT MACHINES
  ...Array.from(
    { length: 10 },
    (_, index) => `slotmachine${index + 1}.png`
  )
].map(r2Image);

const showcaseBuckets = {
  player: {
    id: "player",
    index: "01",
    label: "THE PLAYER PRODUCT",
    title: "A casino experience players recognize as yours.",
    desc:
      "Showcase the customer-facing side of Ethosino: original game interfaces, immersive tables, account flows and personalized presentation. These are product screens from the platform—not concept art for a future casino.",
    images: playerImages,
    bullets: [
      "40+ games and interactive experiences across multiple formats",
      "Casino-style originals alongside familiar table games",
      "Branded discovery, navigation and account presentation",
      "An interface designed for repeat visits, not one-time demos"
    ]
  },

  operator: {
    id: "operator",
    index: "02",
    label: "THE OPERATOR WORKSPACE",
    title: "The decisions behind a casino happen here.",
    desc:
      "The administration product gives a team working surfaces for account review, catalog configuration, payments oversight and operational records. The product gallery will show authentic back-office screens as they are added.",
    images: [],
    bullets: [
      "Review individual accounts and their operational history",
      "Inspect transactions and withdrawal review queues",
      "Update catalog availability and promotional settings",
      "Investigate activity through analytics and recorded events"
    ]
  },

  trust: {
    id: "trust",
    index: "03",
    label: "THE EVIDENCE LAYER",
    title: "A record of what happened—and why it matters.",
    desc:
      "Explore the evidence operators need when reviewing access restrictions, game results and administrative actions. Verification and policy tools help make an incident inspectable rather than a matter of guesswork.",
    images: [],
    bullets: [
      "Location and access decisions tied to applicable policies",
      "Security and audit events available for review",
      "Game-verification material for supported originals",
      "Identity and responsible-play controls where configured"
    ]
  }
};

const platformLayers = [
  {
    id: "experience",
    number: "01",
    icon: MonitorUp,
    eyebrow: "PRESENTATION",
    title: "Player Experience",
    short: "Make the casino feel like your business.",
    description:
      "Set the visible identity: domain, colors, lobby layout, content, game discovery and player-facing flows. A configurable presentation layer lets each operator shape the experience around its audience without commissioning an entirely new frontend.",
    points: [
      "Brand identity and operator domain",
      "Configurable lobby sections and messaging",
      "Account, notifications and player navigation",
      "Rewards and engagement presentation"
    ],
    signal: "BRAND IDENTITY"
  },
  {
    id: "games",
    number: "02",
    icon: Gamepad2,
    eyebrow: "CONTENT",
    title: "Original Game Studio",
    short: "A proprietary catalog with its own development roadmap.",
    description:
      "Ethosino builds and maintains its own portfolio of instant games, digital tables, slots and arcade-inspired experiences. That gives the platform a source of differentiated content rather than depending entirely on a third-party catalog.",
    points: [
      "Original titles developed in-house",
      "Table, instant, slot and arcade formats",
      "Multiplayer and tournament infrastructure",
      "Operator-level game availability settings"
    ],
    signal: "IN-HOUSE STUDIO"
  },
  {
    id: "money",
    number: "03",
    icon: WalletCards,
    eyebrow: "TRANSACTIONS",
    title: "Wallet + Ledger",
    short: "Know the history behind every balance.",
    description:
      "Gameplay produces financial events. The wallet and ledger layer connects balances, settlement records and withdrawal review so staff can investigate a transaction in context—not infer it from a number on a screen.",
    points: [
      "Configurable player wallet models",
      "Recorded game and balance movements",
      "Withdrawal and treasury review surfaces",
      "Payment-provider integration points"
    ],
    signal: "TRACEABLE VALUE"
  },
  {
    id: "trust",
    number: "04",
    icon: ShieldCheck,
    eyebrow: "INTEGRITY",
    title: "Fairness + Controls",
    short: "Put verifiable records behind important decisions.",
    description:
      "Supported game outcomes can be reproduced from cryptographic inputs after seed disclosure. Alongside that, location restrictions, event histories and risk workflows give operators evidence to inspect when questions arise.",
    points: [
      "Commit–reveal outcome verification on supported titles",
      "Jurisdiction-aware access enforcement",
      "Security and administrative activity records",
      "Risk and responsible-gaming workflows"
    ],
    signal: "REVIEWABLE EVIDENCE"
  },
  {
    id: "operations",
    number: "05",
    icon: ServerCog,
    eyebrow: "WORKSPACE",
    title: "Operator Control Plane",
    short: "Give staff one place to act on what they find.",
    description:
      "Day-to-day administration is a working product, not a settings page. Authorized teams can manage players, review withdrawals, configure games and promotions, inspect activity and coordinate operational decisions.",
    points: [
      "Player and account administration",
      "Game, content and promotion configuration",
      "Operational reporting and review queues",
      "Tenant-scoped administration and permissions"
    ],
    signal: "OPERATE WITH CONTEXT"
  },
  {
    id: "copilot",
    number: "06",
    icon: BrainCircuit,
    eyebrow: "ANALYSIS",
    title: "AI Operator Copilot",
    short: "Ask operational questions without exporting spreadsheets.",
    description:
      "The Copilot uses permissioned backend tools to interpret authorized, tenant-scoped records. It helps explain trends and surface questions worth investigating, while account changes, settlement and payout decisions stay with controlled systems and people.",
    points: [
      "Natural-language questions over permitted records",
      "Tenant boundaries enforced by backend tools",
      "Contextual findings for human review",
      "Read-only advisory authority"
    ],
    signal: "AI WITH BOUNDARIES"
  }
];

const gameShowcaseMetadata = {
  "bj-fin.png": {
    name: "Blackjack",
    category: "Table Games",
    description: "A recognizable 21 table with purpose-built digital betting and hand controls."
  },
  "bacccomputer.png": {
    name: "Baccarat",
    category: "Table Games",
    description: "Player and banker decisions presented in a focused electronic table."
  },
  "baccimmersive.png": {
    name: "Immersive Baccarat",
    category: "Immersive Experiences",
    description: "Baccarat staged as a more atmospheric, dealer-led table presentation."
  },
  "crapscraps.png": {
    name: "Craps",
    category: "Table Games",
    description: "The pace and decision-making of craps translated into an interactive dice table."
  },
  "roulroulredone.png": {
    name: "Roulette",
    category: "Table Games",
    description: "A dedicated wheel-and-betting layout for straight, split and other available wagers."
  },
  "warwarwar.png": {
    name: "Casino War",
    category: "Table Games",
    description: "A direct, fast-to-understand card comparison format."
  },
  "tcp-fin.png": {
    name: "Three Card Poker",
    category: "Table Games",
    description: "Three-card hands, table-style actions and poker-inspired decisions."
  },
  "ultimateth.png": {
    name: "Ultimate Texas Hold'em",
    category: "Table Games",
    description: "A digital version of the familiar player-versus-dealer poker table."
  },

  "minesregen.png": {
    name: "Mines",
    category: "Ethosino Originals",
    description: "Select tiles and manage the risk of revealing a hidden mine."
  },
  "plinkoregen.png": {
    name: "Plinko",
    category: "Ethosino Originals",
    description: "Watch each drop resolve through a field of pegs into an outcome slot."
  },
  "bingoregen.png": {
    name: "Bingo",
    category: "Ethosino Originals",
    description: "A number-matching format with a distinct interactive game presentation."
  },
  "caseopeningregen.png": {
    name: "Case Opening",
    category: "Ethosino Originals",
    description: "A suspense-driven reveal mechanic built around virtual prize outcomes."
  },
  "thinkfastregen.png": {
    name: "Think Fast",
    category: "Ethosino Originals",
    description: "Timed responses and quick judgment form the core of this original game."
  },
  "pricediscoveryregen.png": {
    name: "Price Discovery",
    category: "Ethosino Originals",
    description: "Test estimates and decisions in a price-centered original concept."
  },
  "scratchersregen.png": {
    name: "Scratch Cards",
    category: "Ethosino Originals",
    description: "A hands-on reveal interaction inspired by instant-win tickets."
  },
  "pacmanregen.png": {
    name: "Pacmania",
    category: "Arcade Experiences",
    description: "Navigate an arcade-style maze in a casino-adjacent original format."
  },
  "patternremember.png": {
    name: "Pattern Remember",
    category: "Ethosino Originals",
    description: "Watch, remember and reproduce increasingly demanding visual sequences."
  },
  "kenoregen.png": {
    name: "Keno",
    category: "Ethosino Originals",
    description: "Choose numbers and follow the draw through a custom Keno interface."
  },
  "dicerangeregen.png": {
    name: "Dice Range",
    category: "Ethosino Originals",
    description: "Pick an outcome range before the dice result is generated."
  },
  "crashregen.png": {
    name: "Crash",
    category: "Ethosino Originals",
    description: "Follow an increasing multiplier and decide when to cash out."
  },

  "basketballregen.png": {
    name: "Basketball",
    category: "Arcade Experiences",
    description: "An arcade basketball challenge translated into a playable digital title."
  },
  "clawmachineregen.png": {
    name: "Claw Machine",
    category: "Arcade Experiences",
    description: "A virtual arcade claw game with interactive timing and positioning."
  },
  "gravitywellregen.png": {
    name: "Gravity Well",
    category: "Original Concepts",
    description: "An experimental original built around gravity-themed game mechanics."
  },
  "laservaultregen.png": {
    name: "Laser Vault",
    category: "Original Concepts",
    description: "A futuristic arcade concept featuring a laser-security setting."
  },
  "magnetdropregen.png": {
    name: "Magnet Drop",
    category: "Original Concepts",
    description: "A magnetic-themed drop game with its own visual identity."
  },
  "navaldice.png": {
    name: "Naval Dice",
    category: "Original Concepts",
    description: "A naval-themed dice format developed for the Ethosino catalog."
  },
  "strikecircuit.png": {
    name: "Strike Circuit",
    category: "Original Concepts",
    description: "A circuit-inspired arcade concept with a high-energy presentation."
  }
};

const getShowcaseGame = (url, index) => {
  const filename = url.split("/").pop()?.split("?")[0] || "";
  const metadata = gameShowcaseMetadata[filename];

  const slotMatch = filename.match(/^slotmachine(\d+)\.png$/i);

  if (slotMatch) {
    return {
      id: filename,
      image: url,
      name: `Slot Machine ${slotMatch[1]}`,
      category: "Slot Machines",
      description:
        "A themed slot title from the first-party catalog, shown here as a product screenshot."
    };
  }

  return {
    id: filename || `game-${index}`,
    image: url,
    name: metadata?.name || `Product Screen ${index + 1}`,
    category: metadata?.category || "Platform Experience",
    description:
      metadata?.description ||
      "A screenshot from the Ethosino software catalog."
  };
};

const proofSignals = [
  ["40+", "Games & experiences"],
  ["ONE", "Platform core"],
  ["MULTI", "Operator brands"],
  ["AI", "Read-only insights"]
];

const pricingPlans = [
  {
    name: "Pilot Operator Program",
    implementation: "$0 Onboarding",
    licensing: "$0 / month (first 3 months)",
    revShare: "0% (first 3 months)",
    timeline: "1–3 Week Deployment",
    ideal: "Small operators ready to evaluate the platform through a limited, measured pilot.",
    minimumVolume: "No minimum required",
    featured: true,
    features: [
      "Branded platform access and first-party game catalog",
      "One operator brand and domain configuration",
      "Wallet records and supported game-verification tools",
      "Back-office controls, event history and reporting",
      "Configured access, risk and limit controls",
      "Direct collaboration during pilot validation"
    ],
    note:
      "Pilots are accepted selectively. Scope, technical readiness and launch obligations are reviewed before any live deployment; continuation terms are agreed separately."
  },
  {
    name: "Starter Operator License",
    implementation: "$1,500 – $2,500 Setup",
    licensing: "$750 – $1,250 / month",
    revShare: "5% – 8%",
    timeline: "2–4 Week Deployment",
    ideal: "A single-brand operation moving from evaluation toward a production launch.",
    minimumVolume: "$50K+ Monthly GGR Target",
    features: [
      "Single brand / domain",
      "First-party content with a configurable catalog",
      "Branded casino and authorized staff portal",
      "Player wallets and recorded transactions",
      "Outcome verification on supported originals",
      "Operator reports and inspectable events",
      "Updates to the maintained software core"
    ]
  },
  {
    name: "Growth Operator License",
    implementation: "$3,500 – $6,000 Setup",
    licensing: "$1,500 – $3,000 / month",
    revShare: "4% – 7%",
    timeline: "3–5 Week Deployment",
    ideal: "Operators extending a working brand with deeper controls and acquisition tooling.",
    minimumVolume: "$150K+ Monthly GGR Target",
    features: [
      "Support for up to 2–3 configured brands",
      "Affiliate and player referral workflows",
      "Expanded performance and wager reporting",
      "Offers, loyalty and reward configuration",
      "Game availability controlled per tenant",
      "Broader player-safety and risk settings",
      "Capacity reviews and deployment support"
    ]
  },
  {
    name: "Multi-Brand Operator",
    implementation: "$8,000 – $15,000 Setup",
    licensing: "$3,500 – $7,500 / month",
    revShare: "3% – 6% (negotiable)",
    timeline: "4–6 Week Deployment",
    ideal: "Teams managing several differentiated brands from a common technology base.",
    minimumVolume: "$300K+ Monthly GGR Target",
    features: [
      "Up to five operator brands or domains",
      "Tenant-specific access and configuration",
      "Expanded risk review and exposure settings",
      "Configurable currency and wallet setups",
      "Campaign and acquisition administration",
      "More detailed business and activity reporting",
      "Prioritized capacity and operational support"
    ]
  },
  {
    name: "Enterprise Operator",
    implementation: "$20,000 – $50,000+",
    licensing: "$8,000 – $20,000+ / month",
    revShare: "Custom or flat licensing",
    timeline: "5–8 Week Deployment",
    ideal: "Established operators requiring specialized integrations and deeper infrastructure planning.",
    minimumVolume: "$750K+ Monthly GGR",
    features: [
      "Additional brand environments by agreement",
      "Optional dedicated deployment architecture",
      "Custom payment, identity and API integration work",
      "Expanded verification and audit capabilities",
      "Scoped proprietary feature development",
      "Performance engineering for agreed workloads",
      "Dedicated technical engagement"
    ]
  },
  {
    name: "Strategic / Institutional",
    implementation: "Custom Partnership",
    licensing: "Custom",
    revShare: "Strategic structure",
    timeline: "Custom Deployment",
    ideal: "Strategic partners seeking a negotiated development, licensing or distribution relationship.",
    minimumVolume: "$1M+ Monthly GGR",
    features: [
      "Infrastructure designed to the agreed brief",
      "Direct product and engineering collaboration",
      "New game or product work under a defined scope",
      "Documented integration and white-label options",
      "Region-specific deployment architecture",
      "Coordinated plans for operator analytics",
      "Negotiated long-term commercial terms"
    ]
  }
];


const faqItems = [
  {
    q: "What would our operator actually receive?",
    a:
      "A branded casino frontend, access to Ethosino's first-party content, operator administration and the supporting wallet, game-history and configuration systems included in your agreed deployment. Required third-party integrations and launch services are scoped separately."
  },
  {
    q: "Can we choose which games appear on our site?",
    a:
      "Yes. Operators can configure supported game availability and player-facing content for their own brand. Specific titles, availability, jurisdiction restrictions and any outside provider integrations depend on the deployment."
  },
  {
    q: "How much can we customize without a bespoke rebuild?",
    a:
      "Tenant configuration covers core visual identity, domain, catalog and supported content and policy settings. Changes beyond the existing configuration surfaces can be evaluated as separate development work."
  },
  {
    q: "How are our data and administrative access separated?",
    a:
      "The architecture uses tenant context in operator configuration and authorized backend workflows. During diligence, we can walk through the relevant access paths, provisioning process and controls for the proposed environment."
  },
  {
    q: "Who is responsible for payments, KYC and legal clearance?",
    a:
      "The operator is responsible for its legal basis to operate, required approvals and appropriate payment and identity-verification arrangements. Ethosino supplies configurable software and integration surfaces, not a gaming license or legal opinion."
  },
  {
    q: "What needs to happen before our first pilot goes live?",
    a:
      "We first agree on the market, brand, permitted game catalog, wallet model, integrations and commercial scope. The environment is then configured and tested, with access rules, money flows and operational review procedures validated before any authorized production launch."
  }
];

const InfiniteShowcaseGallery = ({
  images = [],
  label = "ETHOSINO"
}) => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const games = useMemo(
    () => images.map((url, index) => getShowcaseGame(url, index)),
    [images]
  );

  const selectedGame =
    selectedIndex !== null ? games[selectedIndex] : null;

  const isOpen = selectedIndex !== null && !!selectedGame;

  const midpoint = Math.ceil(games.length / 2);

  const topRow = games.slice(0, midpoint);
  const bottomRow = games.slice(midpoint);

  const openGame = index => setSelectedIndex(index);
  const closeGame = () => setSelectedIndex(null);

  const navigate = useCallback(
    direction => {
      if (!games.length) return;

      setSelectedIndex(current => {
        if (current === null) return null;

        return (
          current + direction + games.length
        ) % games.length;
      });
    },
    [games.length]
  );

  useEffect(() => {
    setSelectedIndex(null);
  }, [images]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = event => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        navigate(1);
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        navigate(-1);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, navigate]);

  const renderRow = (rowGames, direction, offset) => {
    if (!rowGames.length) return null;

    return (
      <div
        className={`es-showcase-marquee es-showcase-marquee--${direction}`}
      >
        <div className="es-showcase-marquee__track">
          {[0, 1].map(copy => (
            <div
              key={copy}
              className="es-showcase-marquee__group"
              aria-hidden={copy === 1}
            >
              {rowGames.map((game, index) => (
                <button
                  key={`${copy}-${game.id}`}
                  type="button"
                  className="es-showcase-game-card es-showcase-game-card--interactive"
                  onClick={() => openGame(offset + index)}
                  tabIndex={copy === 1 ? -1 : 0}
                  aria-label={`Inspect ${game.name} screenshot`}
                >
                  <div className="es-showcase-game-card__image">
                    <img
                      src={game.image}
                      alt={copy === 0 ? game.name : ""}
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />

                    <div className="es-showcase-game-card__shine" />
                    <div className="es-showcase-game-card__gradient" />

                    <div className="es-showcase-game-card__badge">
                      <span className="es-showcase-game-card__dot" />
                      {game.category}
                    </div>

                    <div className="es-showcase-game-card__expand">
                      <Maximize2 size={17} />
                      <span>VIEW SCREENSHOT</span>
                    </div>
                  </div>

                  <div className="es-showcase-game-card__footer">
                    <div className="es-showcase-game-card__identity">
                      <span className="es-showcase-game-card__eyebrow">
                        {label}
                      </span>
                      <h4>{game.name}</h4>
                    </div>

                    <div className="es-showcase-game-card__arrow">
                      <ArrowRight size={17} />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!games.length) {
    return (
      <div className="es-showcase-empty">
        <span>PRODUCT GALLERY</span>
        <h3>More screenshots are being prepared.</h3>
        <p>
          Ask for a guided look at the relevant operating screens.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="es-showcase-gallery">
        <div className="es-showcase-gallery__ambient es-showcase-gallery__ambient--one" />
        <div className="es-showcase-gallery__ambient es-showcase-gallery__ambient--two" />

        <div className="es-showcase-gallery__header">
          <div className="es-showcase-gallery__status">
            <span className="es-showcase-gallery__status-dot" />
            BROWSE REAL PRODUCT SCREENS
          </div>

          <div className="es-showcase-gallery__count">
            {games.length} IMAGES
          </div>
        </div>

        <div className="es-showcase-gallery__viewport">
          {renderRow(topRow, "left", 0)}
          {renderRow(bottomRow, "right", midpoint)}
        </div>

        <div className="es-showcase-gallery__bottom">
          <span>SCREENSHOTS FROM ETHOSINO</span>
          <span>SELECT A GAME FOR DETAILS</span>
        </div>
      </div>

      <Modal
        open={isOpen}
        onClose={closeGame}
        center
        blockScroll
        closeOnEsc
        closeOnOverlayClick
        showCloseIcon={true}
        focusTrapped={true}
        classNames={{
          root: "es-game-modal-root",
          overlay: "es-game-modal-overlay",
          modalContainer: "es-game-modal-container",
          modal: "es-game-modal",
          closeButton: "es-game-modal-close"
        }}
      >
        {selectedGame && (
          <div className="es-game-modal__content">
            <div className="es-game-modal__top">
              <div className="es-game-modal__brand">
                <div className="es-game-modal__brand-icon">
                  <Gamepad2 size={19} />
                </div>

                <div>
                  <span>ETHOSINO / GAME PORTFOLIO</span>
                  <strong>GAME SCREENSHOT LIBRARY</strong>
                </div>
              </div>

              <div className="es-game-modal__counter">
                {String(selectedIndex + 1).padStart(2, "0")}
                <span>/</span>
                {String(games.length).padStart(2, "0")}
              </div>
            </div>

            <div className="es-game-modal__layout">
              <div className="es-game-modal__visual">
                <div className="es-game-modal__visual-top">
                  <span>
                    <span className="es-game-modal__live-dot" />
                    PRODUCT CAPTURE
                  </span>

                  <span>ETHOSINO</span>
                </div>

                <div className="es-game-modal__image-stage">
                  <img
                    key={selectedGame.id}
                    src={selectedGame.image}
                    alt={`${selectedGame.name} gameplay screenshot`}
                    decoding="async"
                  />
                </div>

                <div className="es-game-modal__visual-bottom">
                  <span>
                    <Images size={15} />
                    ACTUAL GAME SCREENSHOT
                  </span>

                  <span>ENLARGED VIEW</span>
                </div>
              </div>

              <div className="es-game-modal__details">
                <div className="es-game-modal__category">
                  <Sparkles size={14} />
                  {selectedGame.category}
                </div>

                <h2>{selectedGame.name}</h2>

                <p className="es-game-modal__description">
                  {selectedGame.description}
                </p>

                <div className="es-game-modal__divider" />

                <div className="es-game-modal__feature-list">
                  <div>
                    <Layers3 size={18} />
                    <span>
                      <strong>Built as a game product</strong>
                      <small>
                        Designed to run within Ethosino’s player environment.
                      </small>
                    </span>
                  </div>

                  <div>
                    <Gamepad2 size={18} />
                    <span>
                      <strong>Format</strong>
                      <small>{selectedGame.category}</small>
                    </span>
                  </div>

                  <div>
                    <ShieldCheck size={18} />
                    <span>
                      <strong>Operator platform</strong>
                      <small>
                        Explore how the title fits an operator’s catalog.
                      </small>
                    </span>
                  </div>
                </div>

                <a
                  href="https://demo.ethosino.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="es-game-modal__cta"
                  style={{ color: "black" }}
                >
                  OPEN GAME DEMO
                  <ExternalLink size={17} />
                </a>

                <span className="es-game-modal__disclaimer">
                  Screenshot only. Playability and availability depend on the
                  demo environment and applicable access rules.
                </span>
              </div>
            </div>

            <div className="es-game-modal__footer">
              <div className="es-game-modal__navigation">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  aria-label="Previous game"
                >
                  <ChevronLeft size={19} />
                  <span>BACK</span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate(1)}
                  aria-label="Next game"
                >
                  <span>NEXT SCREEN</span>
                  <ChevronRight size={19} />
                </button>
              </div>

              <span className="es-game-modal__hint">
                USE ARROW KEYS TO CHANGE SCREENSHOTS
              </span>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

const revealVariants = {
  hiddenLeft: {
    opacity: 0,
    x: -92,
    y: 24,
    rotateY: 7
  },
  hiddenRight: {
    opacity: 0,
    x: 92,
    y: 24,
    rotateY: -7
  },
  hiddenUp: {
    opacity: 0,
    y: 64
  },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    rotateY: 0,
    transition: {
      duration: 0.72,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};


const Reveal = ({ children, direction = "up", className = "", delay = 0 }) => {
  const reduceMotion = useReducedMotion();
  const hidden =
    direction === "left"
      ? "hiddenLeft"
      : direction === "right"
        ? "hiddenRight"
        : "hiddenUp";

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      variants={revealVariants}
      initial={hidden}
      whileInView="visible"
      viewport={{ once: true, amount: 0.18 }}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
};


const SpotlightCard = ({ children, className = "" }) => {
  const ref = useRef(null);

  const handlePointerMove = event => {
    const node = ref.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    node.style.setProperty("--esv4-x", `${event.clientX - rect.left}px`);
    node.style.setProperty("--esv4-y", `${event.clientY - rect.top}px`);
  };

  return (
    <div
      ref={ref}
      onPointerMove={handlePointerMove}
      className={`ethosino-sales-v4-spotlight ${className}`}
    >
      {children}
    </div>
  );
};


const PlatformModule = ({ module, active, onClick }) => {
  const Icon = module.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`ethosino-sales-v4-layer-button ${active ? "is-active" : ""}`}
    >
      <span className="ethosino-sales-v4-layer-number">{module.number}</span>
      <span className="ethosino-sales-v4-layer-icon"><Icon size={18} /></span>
      <span className="ethosino-sales-v4-layer-labels">
        <small>{module.eyebrow}</small>
        <strong>{module.title}</strong>
      </span>
      <ArrowRight size={15} />
    </button>
  );
};


const App = () => {
  const heroRef = useRef(null);
  const width = useWindowWidth();
  const reduceMotion = useReducedMotion();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeLayer, setActiveLayer] = useState(platformLayers[0]);
  const [activeShowcase, setActiveShowcase] = useState("player");
  const [openFaq, setOpenFaq] = useState(0);
  const [copilotThinking, setCopilotThinking] = useState(false);
  const [copilotAnswered, setCopilotAnswered] = useState(true);

  const [activePricingIndex, setActivePricingIndex] = useState(
    Math.max(0, pricingPlans.findIndex(plan => plan.featured))
  );

  const reducePricingMotion = useReducedMotion();

  const pricingCount = pricingPlans.length;

  const goToPricing = (index) => {
    if (!pricingCount) return;

    setActivePricingIndex(
      ((index % pricingCount) + pricingCount) % pricingCount
    );
  };

  const openPricingDetails = (plan) => {
    setSelectedPlan(plan);
    setSheetOpen(true);
  };

  const getPricingPosition = (index) => {
    if (!pricingCount) return 0;

    let offset = index - activePricingIndex;

    if (offset > pricingCount / 2) {
      offset -= pricingCount;
    }

    if (offset < -pricingCount / 2) {
      offset += pricingCount;
    }

    return offset;
  };

  const { scrollYProgress } = useScroll();
  const progressScale = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 26,
    restDelta: 0.001
  });

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const heroCopyY = useTransform(heroProgress, [0, 1], [0, 78]);
  const heroConsoleY = useTransform(heroProgress, [0, 1], [0, -45]);
  const heroGlowY = useTransform(heroProgress, [0, 1], [0, 160]);

  const settingsTiers = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 650,
      slidesToShow: width <= 775 ? 1 : 2,
      slidesToScroll: 1,
      autoplay: false,
      arrows: false,
      adaptiveHeight: false,
      responsive: [
        { breakpoint: 1200, settings: { slidesToShow: 2 } },
        { breakpoint: 768, settings: { slidesToShow: 1 } }
      ]
    }),
    [width]
  );

  const selectedShowcase = showcaseBuckets[activeShowcase];

  const runCopilotDemo = () => {
    if (copilotThinking) return;

    setCopilotThinking(true);
    setCopilotAnswered(false);

    window.setTimeout(() => {
      setCopilotThinking(false);
      setCopilotAnswered(true);
    }, 1100);
  };

  return (
    <Fragment>
      <HeaderToMainPage />

      <motion.div
        className="ethosino-sales-v4-scroll-progress"
        style={{ scaleX: progressScale }}
      />

      {sheetOpen && selectedPlan && (
        <BottomSheet
          open={sheetOpen}
          blocking
          onDismiss={() => setSheetOpen(false)}
          snapPoints={({ maxHeight }) => [maxHeight * 0.9]}
          expandOnContentDrag={false}
          className="ethosino-sales-sheet-v4"
          header={<div className="ethosino-sales-sheet-v4-grabber" />}
        >
          <div className="ethosino-sales-sheet-v4-content">
            <div className="ethosino-sales-sheet-v4-head">
              <div>
                <span className="ethosino-sales-v4-kicker">PROPOSED DEPLOYMENT OPTION</span>
                <h2>{selectedPlan.name}</h2>
                <p>{selectedPlan.ideal}</p>
              </div>

              <button
                type="button"
                className="ethosino-sales-sheet-v4-close"
                onClick={() => setSheetOpen(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {selectedPlan.note && (
              <div className="ethosino-sales-sheet-v4-note">
                <FileCheck2 size={17} />
                <span>{selectedPlan.note}</span>
              </div>
            )}

            <div className="ethosino-sales-sheet-v4-metrics">
              {[
                ["Implementation", selectedPlan.implementation],
                ["Platform fee", selectedPlan.licensing],
                ["Commercial model", selectedPlan.revShare],
                ["Typical deployment", selectedPlan.timeline]
              ].map(([label, value]) => (
                <div key={label}>
                  <small>{label}</small>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>

            <div className="ethosino-sales-sheet-v4-grid">
              <section>
                <span className="ethosino-sales-v4-kicker">SCOPE AT A GLANCE</span>
                <ul>
                  {selectedPlan.features.map(feature => (
                    <li key={feature}><Check size={15} />{feature}</li>
                  ))}
                </ul>
              </section>

              <section>
                <span className="ethosino-sales-v4-kicker">ONBOARDING SEQUENCE</span>
                {[
                  "Confirm market and operating model",
                  "Create and activate the tenant",
                  "Configure the branded experience",
                  "Connect agreed service providers",
                  "Test launch-critical workflows"
                ].map((item, index) => (
                  <div className="ethosino-sales-sheet-v4-pipeline" key={item}>
                    <b>0{index + 1}</b>
                    <span>{item}</span>
                  </div>
                ))}
              </section>
            </div>

            <div className="ethosino-sales-sheet-v4-volume">
              <small>Suggested operating profile</small>
              <strong>{selectedPlan.minimumVolume}</strong>
            </div>

            <div className="ethosino-sales-sheet-v4-actions">
              <Link
                to={selectedPlan.name.includes("Pilot") ? "/request-documentation" : "/book-demo"}
                className="ethosino-sales-v4-button ethosino-sales-v4-button--primary"
              >
                {selectedPlan.name.includes("Pilot") ? "Discuss a Pilot" : "Discuss This Option"}
                <ArrowRight size={16} />
              </Link>

              <Link
                to="/book-demo"
                className="ethosino-sales-v4-button ethosino-sales-v4-button--ghost"
              >
                View the Software Together
              </Link>
            </div>
          </div>
        </BottomSheet>
      )}

      <main className="ethosino-sales-v4" id="ethosino-sales-home-v4">
        <section
          className="ethosino-sales-v4-hero"
          id="ethosino-sales-hero-v4"
          ref={heroRef}
        >
          <video className="ethosino-sales-v4-hero-video" autoPlay muted loop playsInline>
            <source src={require("../../assets/video/slots.mp4")} type="video/mp4" />
          </video>

          <div className="ethosino-sales-v4-hero-scrim" />
          <div className="ethosino-sales-v4-hero-gridlines" />

          {width >= 825 && (
            <SalesInfrastructureField
              primary="#24e7c3"
              secondary="#27b8ef"
              accent="#a78bfa"
              nodeCount={168}
              connectionDistance={155}
              pulseCount={9}
              maxFps={30}
            />
          )}

          <motion.div
            className="ethosino-sales-v4-hero-orb ethosino-sales-v4-hero-orb--a"
            style={reduceMotion ? undefined : { y: heroGlowY }}
          />
          <div className="ethosino-sales-v4-hero-orb ethosino-sales-v4-hero-orb--b" />


          <div className="ethosino-sales-v4-shell">
            <div className="ethosino-sales-v4-hero-layout es-hero-upgraded">

              {/* ==============================================
        LEFT — PRIMARY COMMERCIAL MESSAGE
    ============================================== */}

              <motion.div
                className="ethosino-sales-v4-hero-copy es-hero-copy"
                style={reduceMotion ? undefined : { y: heroCopyY }}
                initial={
                  reduceMotion
                    ? false
                    : { opacity: 0, x: -65, filter: "blur(8px)" }
                }
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.9,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >

                <div className="ethosino-sales-v4-live-chip es-hero-chip">
                  <span />
                  PROPRIETARY CASINO SOFTWARE FOR OPERATORS
                </div>


                <div className="es-hero-heading-group">
                  <h1 className="es-hero-headline">
                    Your brand.
                    <br />
                    Your casino.

                    <span className="es-hero-headline__promise">
                      Powered by a
                      <br />
                      proprietary platform.
                    </span>
                  </h1>

                  <div className="es-hero-typewriter-row">
                    <span className="es-hero-typewriter-label">
                      WHAT YOU GET
                    </span>

                    <EthosinoTypewriter
                      phrases={[
                        "A distinctive player experience.",
                        "Original games from our studio.",
                        "Administration designed for operators.",
                        "An onboarding path from brand to pilot."
                      ]}
                      typingSpeed={55}
                      eraseSpeed={28}
                      holdDelay={2300}
                      betweenDelay={350}
                      className="es-hero-typewriter"
                    />
                  </div>
                  <div style={{ height: 17.215 }} />
                </div>

                <p className="ethosino-sales-v4-hero-lead es-hero-lead">
                  Ethosino provides the casino frontend, original games
                  and operational software behind your brand. Built for
                  sweepstakes-style deployments, the platform gives you
                  a configurable starting point while keeping the
                  commercial business and its decisions in your hands.
                </p>

                <div className="es-hero-value-line">
                  <div>
                    <Check size={15} />
                    Configure your own brand
                  </div>

                  <div>
                    <Check size={15} />
                    Manage the operating day
                  </div>

                  <div>
                    <Check size={15} />
                    Start with working software
                  </div>
                </div>

                <div className="ethosino-sales-v4-hero-actions es-hero-actions">
                  <a
                    href="https://demo.ethosino.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ethosino-sales-v4-button ethosino-sales-v4-button--primary es-hero-primary"
                  >
                    OPEN GAME DEMO
                    <ArrowRight size={18} />
                  </a>

                  <Link
                    to="/book-demo"
                    className="ethosino-sales-v4-button ethosino-sales-v4-button--ghost es-hero-secondary"
                  >
                    SCHEDULE A PRODUCT WALKTHROUGH
                    <ArrowRight size={17} />
                  </Link>
                </div>

                <div className="es-hero-bottom-note">
                  <span className="es-hero-bottom-note__line" />
                  A first-party software product, available to explore today.
                </div>

                <div className="ethosino-sales-v4-proof-strip es-hero-proof">
                  {proofSignals.map(([value, label], index) => (
                    <motion.div
                      key={label}
                      initial={
                        reduceMotion ? false : { opacity: 0, y: 20 }
                      }
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: 0.45 + index * 0.09,
                        duration: 0.55
                      }}
                    >
                      <strong>{value}</strong>
                      <span>{label}</span>
                    </motion.div>
                  ))}
                </div>
                {width <= 825 ? <div style={{ height: 22.25 }} /> : null}
              </motion.div>

              {/* ==============================================
        RIGHT — PLATFORM VISUALIZATION
    ============================================== */}

              <motion.aside
                className="ethosino-sales-v4-hero-console es-hero-console"
                style={reduceMotion ? undefined : { y: heroConsoleY }}
                initial={
                  reduceMotion
                    ? false
                    : { opacity: 0, x: 65, rotateY: -5 }
                }
                animate={{ opacity: 1, x: 0, rotateY: 0 }}
                transition={{
                  duration: 1,
                  delay: 0.12,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >

                <div className="ethosino-sales-v4-console-topline es-hero-console-top">
                  <div>
                    <span className="ethosino-sales-v4-kicker">
                      PRODUCT / ENGINEERING / OPERATIONS
                    </span>

                    <h3>
                      One product. Two sides of the business.
                    </h3>
                  </div>

                  <div className="ethosino-sales-v4-console-live">
                    <i />
                    DEMO ACCESS
                  </div>
                </div>

                {/* HERO IMAGE COLLAGE */}

                <div className="es-hero-product-visual">

                  <div className="es-hero-product-heading">
                    <div>
                      <span>01 / GAME STUDIO</span>
                      <strong>Selected original and table titles</strong>
                    </div>

                    <span className="es-hero-product-count">
                      40+ TITLES
                    </span>
                  </div>

                  <div className="es-hero-game-grid">

                    {[
                      {
                        name: "Crash",
                        image: "crashregen.png"
                      },
                      {
                        name: "Immersive Baccarat",
                        image: "baccimmersive.png"
                      },
                      {
                        name: "Mines",
                        image: "minesregen.png"
                      },
                      {
                        name: "Roulette",
                        image: "roulroulredone.png"
                      }
                    ].map(game => (
                      <div
                        className="es-hero-game-tile"
                        key={game.name}
                      >
                        <img
                          src={`https://pub-93a1c3f08d9142d8b81cbf054b4cbe0d.r2.dev/${game.image}`}
                          alt={`Ethosino ${game.name}`}
                          loading="eager"
                        />

                        <span>{game.name}</span>
                      </div>
                    ))}
                  </div>

                  {/* OPERATOR CONTROL LAYER */}

                  <div className="es-hero-operator-layer">
                    <div className="es-hero-operator-layer__header">
                      <div>
                        <span>02 / ADMIN WORKSPACE</span>
                        <strong>Built for the people running it</strong>
                      </div>

                      <Layers3 size={20} />
                    </div>

                    <div className="es-hero-operator-modules">
                      {[
                        [WalletCards, "Wallets"],
                        [ShieldCheck, "Review Tools"],
                        [Network, "Tenant Setup"],
                        [BrainCircuit, "AI Copilot"]
                      ].map(([Icon, label]) => (
                        <div key={label}>
                          <Icon size={17} />
                          <span>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* SYSTEM STATUS STRIP */}

                <div className="ethosino-sales-v4-console-modules es-hero-system-strip">
                  {[
                    [Gamepad2, "Game Studio", "IN-HOUSE"],
                    [WalletCards, "Wallet Records", "TRACEABLE"],
                    [ShieldCheck, "Verification", "SUPPORTED"],
                    [BrainCircuit, "AI Copilot", "ADVISORY"]
                  ].map(([Icon, label, state]) => (
                    <div key={label}>
                      <Icon size={15} />
                      <span>{label}</span>
                      <strong>{state}</strong>
                    </div>
                  ))}
                </div>

                <motion.div
                  className="ethosino-sales-v4-console-float ethosino-sales-v4-console-float--one"
                  animate={
                    reduceMotion
                      ? undefined
                      : { y: [0, -8, 0], rotate: [0, 1, 0] }
                  }
                  transition={{
                    duration: 5.4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <BadgeCheck size={14} />
                  CONFIGURABLE
                </motion.div>

                <motion.div
                  className="ethosino-sales-v4-console-float ethosino-sales-v4-console-float--two"
                  animate={
                    reduceMotion
                      ? undefined
                      : { y: [0, 8, 0], rotate: [0, -1, 0] }
                  }
                  transition={{
                    duration: 4.8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Zap size={14} />
                  PRODUCT DEMO
                </motion.div>
              </motion.aside>
            </div>
          </div>


          <div className="ethosino-sales-v4-hero-bottom-fade" />
        </section>

        <section className="ethosino-sales-v4-marquee" aria-label="Ethosino platform capabilities">
          <div className="ethosino-sales-v4-marquee-track">
            {[...Array(2)].flatMap((_, repeatIndex) => [
              "CUSTOM BRANDING",
              "IN-HOUSE GAME STUDIO",
              "INSTANT + TABLE GAMES",
              "AUTOMATED ONBOARDING",
              "OPERATOR WORKSPACE",
              "WALLET HISTORY",
              "TOURNAMENT INFRASTRUCTURE",
              "SEED VERIFICATION",
              "PERMISSIONED AI",
              "CONFIGURABLE DEPLOYMENTS"
            ].map(label => (
              <span key={`${repeatIndex}-${label}`}>
                <i>◆</i>{label}
              </span>
            )))}
          </div>
        </section>


        <section
          className="ethosino-sales-v4-section es-turnkey-section"
          id="ethosino-sales-problem-v4"
        >
          {/* Atmospheric background */}
          <div className="es-turnkey-atmosphere" aria-hidden="true">
            <div className="es-turnkey-grid" />
            <div className="es-turnkey-glow es-turnkey-glow--a" />
            <div className="es-turnkey-glow es-turnkey-glow--b" />
            <div className="es-turnkey-noise" />
          </div>

          <div className="ethosino-sales-v4-shell es-turnkey-shell">

            {/* INTRO */}
            <div className="es-turnkey-intro">
              <Reveal direction="up">
                <div className="es-turnkey-eyebrow">
                  <span className="es-turnkey-eyebrow__line" />
                  FROM AN IDEA TO AN OPERATOR ENVIRONMENT
                  <span className="es-turnkey-eyebrow__index">/ 02</span>
                </div>

                <h2 className="es-turnkey-headline">
                  Build a brand.
                  <span>Not a backend.</span>
                  <em>
                    Start with the software.
                    <br />
                    Shape the business.
                  </em>
                </h2>

                <p className="es-turnkey-lead">
                  A new operator shouldn't have to commission a lobby,
                  develop dozens of games and invent the administrative
                  tooling before testing its market. Ethosino begins
                  with a configured tenant, then adapts branding,
                  policies and game access to the proposed operation.
                </p>

                <div className="es-turnkey-intro-actions">
                  <a
                    href="https://demo.ethosino.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="es-turnkey-primary"
                  >
                    TRY THE DEMONSTRATION
                    <ArrowRight size={19} />
                  </a>

                  <a
                    href="#ethosino-sales-platform-v4"
                    className="es-turnkey-secondary"
                  >
                    INSPECT THE PLATFORM LAYERS
                    <ChevronDown size={18} />
                  </a>
                </div>
              </Reveal>
            </div>

            {/* CINEMATIC PRODUCT STAGE */}
            <div className="es-turnkey-stage">

              <div
                className="es-turnkey-stage__orbit"
                aria-hidden="true"
              />

              <div className="es-turnkey-stage__topline">
                <span>
                  <i />
                  CONFIGURABLE LAUNCH WORKSPACE
                </span>
                <span>
                  EACH BRAND GETS ITS OWN CONFIGURATION
                </span>
              </div>

              {/* LEFT FLOATING PRODUCT CARD */}
              <div className="es-turnkey-float es-turnkey-float--games">
                <Parallax speed={reduceMotion ? 0 : -8}>
                  <div className="es-turnkey-mini-card es-turnkey-mini-card--games">
                    <div className="es-turnkey-mini-card__head">
                      <span>
                        <Gamepad2 size={15} />
                        ORIGINAL CONTENT
                      </span>
                      <i />
                    </div>

                    <div className="es-turnkey-games">
                      <img src={r2Image("bj-fin.png")} alt="Ethosino Blackjack" />
                      <img src={r2Image("baccimmersive.png")} alt="Immersive Baccarat" />
                      <img src={r2Image("minesregen.png")} alt="Ethosino Mines" />
                    </div>

                    <strong>40+ games and experiences</strong>
                    <small>
                      In-house originals, traditional tables,
                      themed slots and experimental game formats.
                    </small>
                  </div>
                </Parallax>
              </div>

              {/* RIGHT FLOATING PROVISIONING CARD */}
              <div className="es-turnkey-float es-turnkey-float--deploy">
                <Parallax speed={9}>
                  <div className="es-turnkey-mini-card es-turnkey-mini-card--deploy">
                    <div className="es-turnkey-mini-card__head">
                      <span>
                        <Network size={15} />
                        TENANT ACTIVATION
                      </span>
                      <i />
                    </div>

                    <div className="es-turnkey-deploy-list">
                      {[
                        "Create tenant context",
                        "Apply brand identity",
                        "Activate staff access",
                        "Set operating policies"
                      ].map((item, index) => (
                        <div key={item}>
                          <span className="es-turnkey-deploy-check">
                            <Check size={11} />
                          </span>
                          <span>{item}</span>
                          <b>0{index + 1}</b>
                        </div>
                      ))}
                    </div>

                    <div className="es-turnkey-deploy-status">
                      <span />
                      STRUCTURED ACTIVATION
                    </div>
                  </div>
                </Parallax>
              </div>

              {/* CENTRAL PLATFORM CONSOLE */}
              <div className="es-turnkey-main-visual">
                <Parallax speed={-4}>
                  <div className="es-turnkey-console">
                    <div className="es-turnkey-console__chrome">
                      <div className="es-turnkey-window-dots">
                        <i /><i /><i />
                      </div>

                      <span>
                        ETHOSINO / FIRST-PARTY SLOT CAPTURE
                      </span>

                      <b>
                        <span />
                        SOFTWARE PREVIEW
                      </b>
                    </div>

                    <div className="es-turnkey-console__screen">
                      <img
                        src={"https://pub-93a1c3f08d9142d8b81cbf054b4cbe0d.r2.dev/slotmachine1.png"}
                        alt="Screenshot of an Ethosino slot game"
                        loading="lazy"
                      />

                      <div className="es-turnkey-console__reflection" />
                    </div>

                    <div className="es-turnkey-console__footer">
                      <div>
                        <span>PRODUCT FOUNDATION</span>
                        <strong>Shared maintained core</strong>
                      </div>

                      <div>
                        <span>CONFIGURATION</span>
                        <strong>Brand-specific settings</strong>
                      </div>

                      <div>
                        <span>OPERATOR SUPPORT</span>
                        <strong>Available data tools</strong>
                      </div>
                    </div>
                  </div>
                </Parallax>
              </div>

              {/* LOWER LEFT WALLET CARD */}
              <div className="es-turnkey-float es-turnkey-float--wallet">
                <Parallax speed={7}>
                  <div className="es-turnkey-mini-card es-turnkey-mini-card--compact">
                    <div className="es-turnkey-feature-icon">
                      <WalletCards size={21} />
                    </div>
                    <div>
                      <strong>Financial records in context</strong>
                      <small>
                        Review game settlement alongside balance movements.
                      </small>
                    </div>
                    <span className="es-turnkey-feature-indicator" />
                  </div>
                </Parallax>
              </div>

              {/* LOWER RIGHT AI CARD */}
              <div className="es-turnkey-float es-turnkey-float--ai">
                <Parallax speed={-10}>
                  <div className="es-turnkey-mini-card es-turnkey-mini-card--ai">
                    <div className="es-turnkey-mini-card__head">
                      <span>
                        <BrainCircuit size={15} />
                        READ-ONLY AI COPILOT
                      </span>
                      <i />
                    </div>

                    <div className="es-turnkey-ai-message">
                      <Sparkles size={17} />
                      <span>
                        Ask what the data shows. Keep decisions with staff.
                      </span>
                    </div>

                    <div className="es-turnkey-ai-tags">
                      <span>TRENDS</span>
                      <span>EXCEPTIONS</span>
                      <span>HISTORY</span>
                    </div>
                  </div>
                </Parallax>
              </div>
            </div>

            {/* POSITIONING STATEMENT */}
            <div className="es-turnkey-statement">
              <Parallax speed={-3}>
                <span className="es-turnkey-statement__label">
                  A BETTER STARTING POINT
                </span>

                <h3>
                  Less time assembling software.
                  <br />

                  <EthosinoTypewriter
                    phrases={[
                      "Choose your catalog.",
                      "Set your policies.",
                      "Validate your pilot."
                    ]}
                    typingSpeed={65}
                    eraseSpeed={35}
                    holdDelay={2200}
                    betweenDelay={350}
                  />

                  <br />
                  More attention on the operation.
                </h3>

                <p>
                  A pilot should test the experience, the operational
                  workflows and the operator's market assumptions—not
                  the basic feasibility of building casino software.
                  That's where a ready-to-configure product changes
                  the starting point.
                </p>
              </Parallax>
            </div>

            {/* FOUR CAPABILITY PILLARS */}
            <div className="es-turnkey-pillars">

              <div className="es-turnkey-pillar">
                <div className="es-turnkey-pillar__top">
                  <span>01 / DIFFERENTIATION</span>
                  <Gamepad2 size={21} />
                </div>
                <div>
                  <h3>Titles you can make part of your identity.</h3>
                  <p>
                    Give players formats they won't find on every
                    identical white-label lobby: from custom instant
                    games to immersive tables and original arcade ideas.
                  </p>
                </div>
                <span className="es-turnkey-pillar__bottom">
                  DISTINCTIVE GAME LIBRARY
                  <ArrowRight size={15} />
                </span>
              </div>

              <div className="es-turnkey-pillar">
                <div className="es-turnkey-pillar__top">
                  <span>02 / REPEATABILITY</span>
                  <Network size={21} />
                </div>
                <div>
                  <h3>A second brand doesn't require a rewrite.</h3>
                  <p>
                    Provisioning creates a separate operator context
                    with its own administration and configuration.
                    The platform can be maintained without treating
                    every new brand as a new software project.
                  </p>
                </div>
                <span className="es-turnkey-pillar__bottom">
                  REUSABLE ENGINEERING
                  <ArrowRight size={15} />
                </span>
              </div>

              <div className="es-turnkey-pillar">
                <div className="es-turnkey-pillar__top">
                  <span>03 / ACCOUNTABILITY</span>
                  <ShieldCheck size={21} />
                </div>
                <div>
                  <h3>Give every review a starting point.</h3>
                  <p>
                    When an account or transaction needs attention,
                    staff should be able to locate the relevant records,
                    inspect the available evidence and take an
                    authorized next step.
                  </p>
                </div>
                <span className="es-turnkey-pillar__bottom">
                  REVIEWABLE OPERATIONS
                  <ArrowRight size={15} />
                </span>
              </div>

              <div className="es-turnkey-pillar">
                <div className="es-turnkey-pillar__top">
                  <span>04 / DECISION SUPPORT</span>
                  <BrainCircuit size={21} />
                </div>
                <div>
                  <h3>Make operational data easier to question.</h3>
                  <p>
                    Explore why wagering shifted, which games gained
                    traction or what events deserve review. AI can
                    interpret permitted records without being allowed
                    to move money or decide game results.
                  </p>
                </div>
                <span className="es-turnkey-pillar__bottom">
                  INFORMED HUMAN DECISIONS
                  <ArrowRight size={15} />
                </span>
              </div>
            </div>

            {/* CLOSING CTA */}
            <div className="es-turnkey-closing">
              <div>
                <span>YOUR PILOT SHOULD ANSWER REAL QUESTIONS</span>
                <h3>
                  Test the brand.
                  <br />
                  <em>Learn from the operation.</em>
                </h3>
                <p>
                  Start with a defined audience, permitted market
                  and measurable launch scope. We'll help map the
                  technical work needed before real customers arrive.
                </p>
              </div>

              <Link to="/book-demo" className="es-turnkey-closing__cta">
                PLAN AN OPERATOR WALKTHROUGH
                <ArrowRight size={19} />
              </Link>
            </div>

            <p className="es-turnkey-legal-note">
              Software demonstration and provisioning do not authorize
              gaming activity. Production operation requires appropriate
              operator-led legal review and approved integrations.
            </p>
          </div>
        </section>


        <section className="ethosino-sales-v4-section ethosino-sales-v4-section--alt" id="ethosino-sales-platform-v4">
          <div className="ethosino-sales-v4-shell">
            <Reveal direction="right" className="ethosino-sales-v4-section-head">
              <span className="ethosino-sales-v4-kicker">EXPLORE THE PRODUCT ARCHITECTURE</span>
              <h2>See how a player action becomes an operator record.</h2>
              <p>
                Follow the responsibilities behind an account, wager or administrative review. Select a layer to understand what it owns, what it records and how it supports the rest of the application.
              </p>
            </Reveal>

            <div className="ethosino-sales-v4-layer-explorer">
              <Reveal direction="left" className="ethosino-sales-v4-layer-menu">
                {platformLayers.map(module => (
                  <PlatformModule
                    key={module.id}
                    module={module}
                    active={activeLayer.id === module.id}
                    onClick={() => setActiveLayer(module)}
                  />
                ))}
              </Reveal>

              <Reveal direction="right" className="ethosino-sales-v4-layer-stage">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeLayer.id}
                    className="ethosino-sales-v4-layer-detail"
                    initial={reduceMotion ? false : { opacity: 0, x: 34, filter: "blur(8px)" }}
                    animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                    exit={reduceMotion ? undefined : { opacity: 0, x: -24, filter: "blur(6px)" }}
                    transition={{ duration: 0.32 }}
                  >
                    <div className="ethosino-sales-v4-layer-detail-top">
                      <div className="ethosino-sales-v4-layer-detail-icon">
                        {React.createElement(activeLayer.icon, { size: 25 })}
                      </div>
                      <div>
                        <span>{activeLayer.number} / {activeLayer.eyebrow}</span>
                        <h3>{activeLayer.title}</h3>
                      </div>
                      <strong>{activeLayer.signal}</strong>
                    </div>

                    <h4>{activeLayer.short}</h4>
                    <p>{activeLayer.description}</p>

                    <div className="ethosino-sales-v4-layer-points">
                      {activeLayer.points.map((point, index) => (
                        <motion.div
                          key={point}
                          initial={reduceMotion ? false : { opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.08 + index * 0.055 }}
                        >
                          <span>0{index + 1}</span>
                          <p>{point}</p>
                          <Check size={14} />
                        </motion.div>
                      ))}
                    </div>

                    <div className="ethosino-sales-v4-layer-signal-map">
                      <span>SESSION</span><i /><span>GAME</span><i /><span>RECORD</span><i /><span>REVIEW</span>
                      <motion.b
                        animate={reduceMotion ? undefined : { left: ["2%", "88%"] }}
                        transition={{ duration: 3.4, repeat: Infinity, ease: "linear" }}
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="ethosino-sales-v4-section" id="ethosino-sales-demo-v4">
          <div className="ethosino-sales-v4-shell">
            <Reveal direction="left" className="ethosino-sales-v4-section-head">
              <span className="ethosino-sales-v4-kicker">OPEN THE DEMO</span>
              <h2>Explore an application, not just a feature list.</h2>
              <p>
                Browse the available game interfaces, navigate the player experience and see how the demo behaves. For administrative screens or deployment-specific questions, request a guided technical walkthrough.
              </p>
            </Reveal>

            <Reveal direction="right" className="ethosino-sales-v4-embedded-frame">
              <div className="ethosino-sales-v4-frame-chrome">
                <div><span /><span /><span /></div>
                <small>DEMONSTRATION ENVIRONMENT</small>
                <b><i /> DEMO</b>
              </div>
              <EthosinoDemoSection />
            </Reveal>
          </div>
        </section>

        <section className="ethosino-sales-v4-section ethosino-sales-v4-section--dark" id="ethosino-sales-architecture-v4">
          <div className="ethosino-sales-v4-shell">
            <Reveal direction="right" className="ethosino-sales-v4-section-head ethosino-sales-v4-section-head--split">
              <div>
                <span className="ethosino-sales-v4-kicker">ONE CODEBASE / DISTINCT OPERATORS</span>
                <h2>New brands without starting at zero.</h2>
              </div>
              <p>
                A tenant isn't a different-colored homepage. It has a defined operating context: its domain, settings, accessible game catalog and authorized administration. The provisioning workflow establishes that context before activation.
              </p>
            </Reveal>

            <div className="ethosino-sales-v4-embedded-frame ethosino-sales-v4-embedded-frame--architecture">
              <EthosinoArchitectureSection />
            </div>
          </div>
        </section>

        <section className="ethosino-sales-v4-section" id="ethosino-sales-control-v4">
          <div className="ethosino-sales-v4-shell">
            <Reveal direction="left" className="ethosino-sales-v4-section-head">
              <span className="ethosino-sales-v4-kicker">THE ADMINISTRATIVE PRODUCT</span>
              <h2>Built for the work between deposits and decisions.</h2>
              <p>
                Use the interactive explorer below to examine the actual areas a team needs to operate: account review, financial investigation, catalog changes, risk events and technical oversight. Each has a defined role and access boundary.
              </p>
            </Reveal>

            <div className="ethosino-sales-v4-embedded-frame ethosino-sales-v4-embedded-frame--capabilities">
              <EthosinoCapabilities />
            </div>
          </div>
        </section>

        <section
          className="ethosino-sales-v4-section ethosino-sales-v4-section--alt es-showcase-section"
          id="ethosino-sales-showcase-v4"
        >
          <div className="ethosino-sales-v4-shell">
            <Reveal
              direction="right"
              className="ethosino-sales-v4-section-head"
            >
              <span className="ethosino-sales-v4-kicker">
                THREE WAYS TO INSPECT THE PRODUCT
              </span>

              <h2>
                See the interfaces from three different perspectives.
              </h2>

              <p>
                Start with player-facing gameplay, then examine the
                management tasks and the evidence behind critical
                activity. Each view answers a different buyer question.
              </p>
            </Reveal>

            <div className="ethosino-sales-v4-showcase-tabs">
              {Object.values(showcaseBuckets).map(bucket => (
                <button
                  key={bucket.id}
                  type="button"
                  className={
                    activeShowcase === bucket.id ? "is-active" : ""
                  }
                  onClick={() => setActiveShowcase(bucket.id)}
                >
                  <span>{bucket.index}</span>
                  <b>{bucket.label}</b>
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={selectedShowcase.id}
                className="es-showcase-content"
                initial={
                  reduceMotion
                    ? false
                    : { opacity: 0, y: 22 }
                }
                animate={{ opacity: 1, y: 0 }}
                exit={
                  reduceMotion
                    ? undefined
                    : { opacity: 0, y: -12 }
                }
                transition={{ duration: 0.35 }}
              >
                <InfiniteShowcaseGallery
                  images={selectedShowcase.images}
                  label={selectedShowcase.label}
                />

                <div className="es-showcase-information">
                  <div className="es-showcase-information__intro">
                    <span className="ethosino-sales-v4-kicker">
                      {selectedShowcase.label}
                    </span>

                    <h3>{selectedShowcase.title}</h3>

                    <p>{selectedShowcase.desc}</p>
                  </div>

                  <div className="es-showcase-information__features">
                    {selectedShowcase.bullets.map((bullet, index) => (
                      <div
                        className="es-showcase-information__feature"
                        key={bullet}
                      >
                        <span className="es-showcase-information__index">
                          0{index + 1}
                        </span>

                        <p>{bullet}</p>

                        <Check size={17} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="es-showcase-stat-strip">
                  <div>
                    <small>SCREENSHOTS</small>
                    <strong>{selectedShowcase.images.length}</strong>
                  </div>

                  <div>
                    <small>CONFIGURATION</small>
                    <strong>Operator-specific</strong>
                  </div>

                  <div>
                    <small>VIEW</small>
                    <strong>Product Demo</strong>
                  </div>

                  <a
                    href="https://demo.ethosino.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    BROWSE THE WORKING DEMO
                    <ArrowRight size={17} />
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </section>

        <section className="ethosino-sales-v4-section ethosino-sales-v4-ai-section" id="ethosino-sales-ai-v4">
          <div className="ethosino-sales-v4-ai-gridlines" />
          <div className="ethosino-sales-v4-shell">
            <div className="ethosino-sales-v4-ai-layout">
              <Reveal direction="left" className="ethosino-sales-v4-ai-copy">
                <span className="ethosino-sales-v4-kicker">ASK BETTER OPERATIONAL QUESTIONS</span>
                <h2>From a question to a focused investigation.</h2>
                <p>
                  Ask about changes in wagering, customer activity or game performance in plain language. The Copilot draws on authorized backend tools and available tenant records to help staff make sense of the data—without unrestricted database access.
                </p>

                <blockquote>
                  <BrainCircuit size={19} />
                  <div>
                    <strong>Analysis is not authority.</strong>
                    <span>The model can suggest an investigation; controlled services and authorized people decide what happens next.</span>
                  </div>
                </blockquote>

                <div className="ethosino-sales-v4-ai-guardrails">
                  {[
                    [LockKeyhole, "Cannot determine outcomes"],
                    [WalletCards, "Cannot edit wallet balances"],
                    [Fingerprint, "Cannot access unrevealed seeds"],
                    [Scale, "Cannot approve identity or payouts"]
                  ].map(([Icon, label]) => (
                    <div key={label}><Icon size={15} /><span>{label}</span></div>
                  ))}
                </div>

                <Link to="/book-demo" className="ethosino-sales-v4-button ethosino-sales-v4-button--primary">
                  Explore the Copilot <ArrowRight size={16} />
                </Link>
              </Reveal>

              <Reveal direction="right" className="ethosino-sales-v4-copilot-console">
                <div className="ethosino-sales-v4-copilot-head">
                  <div>
                    <span><Bot size={16} /> OPERATOR COPILOT / EXAMPLE</span>
                    <small>READ ONLY • PERMISSIONED TOOLS • HUMAN REVIEW</small>
                  </div>
                  <b><i /> SAMPLE</b>
                </div>

                <div className="ethosino-sales-v4-copilot-query">
                  <small>EXAMPLE QUESTION</small>
                  <p>Which of our games changed most in wagering volume this week?</p>
                  <button type="button" onClick={runCopilotDemo} aria-label="Replay sample analysis">
                    <ArrowRight size={16} />
                  </button>
                </div>

                <div className="ethosino-sales-v4-copilot-scopes">
                  {["GAME TRENDS", "WAGERING", "TIME PERIODS", "SEGMENTS", "CONTEXT"].map(scope => <span key={scope}>{scope}</span>)}
                </div>

                <AnimatePresence mode="wait">
                  {copilotThinking ? (
                    <motion.div
                      key="thinking"
                      className="ethosino-sales-v4-copilot-thinking"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div><i /><i /><i /></div>
                      <span>Preparing an illustrative response…</span>
                    </motion.div>
                  ) : copilotAnswered ? (
                    <motion.div
                      key="answer"
                      className="ethosino-sales-v4-copilot-answer"
                      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="ethosino-sales-v4-copilot-answer-head">
                        <span>ILLUSTRATIVE ANALYSIS</span>
                        <strong>REVIEW SUGGESTED</strong>
                      </div>
                      <h4>A game-level comparison can help explain where wagering activity shifted.</h4>
                      <div className="ethosino-sales-v4-copilot-findings">
                        <div><span>01</span><p>Compare total wagers and unique players by game against the previous period.</p></div>
                        <div><span>02</span><p>Separate higher engagement from changes in average wager size.</p></div>
                        <div><span>03</span><p>Check promotions, catalog updates and traffic sources before drawing conclusions.</p></div>
                      </div>
                      <small className="ethosino-sales-v4-copilot-limit">
                        Illustrative response—not a live query or actual business finding. Real analysis uses permitted records for the signed-in tenant.
                      </small>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="ethosino-sales-v4-section" id="ethosino-sales-proof-v4">
          <div className="ethosino-sales-v4-shell">
            <Reveal direction="left" className="ethosino-sales-v4-section-head ethosino-sales-v4-section-head--split">
              <div>
                <span className="ethosino-sales-v4-kicker">PROVABLY FAIR / SUPPORTED ORIGINALS</span>
                <h2>A claim of fairness should be testable.</h2>
              </div>
              <p>
                For eligible games, Ethosino publishes a cryptographic commitment before resolution and makes the required inputs available for later verification. That lets a player reproduce a result under the relevant rules rather than simply accept a statement that it was fair.
              </p>
            </Reveal>

            <div className="ethosino-sales-v4-proof-layout">
              <Reveal direction="left" className="ethosino-sales-v4-proof-flow">
                {[
                  ["01", "COMMIT", "Record a SHA-256 commitment to the undisclosed server seed."],
                  ["02", "RESOLVE", "Generate an outcome from the defined seed, nonce and rules."],
                  ["03", "REVEAL", "Disclose the completed seed when its rotation permits verification."],
                  ["04", "REPRODUCE", "Reproduce the result and compare it with the recorded round."]
                ].map(([number, title, text], index) => (
                  <motion.div
                    key={title}
                    className="ethosino-sales-v4-proof-node"
                    initial={reduceMotion ? false : { opacity: 0, x: index % 2 === 0 ? -55 : 55 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 0.55, delay: index * 0.08 }}
                  >
                    <span>{number}</span>
                    <div><strong>{title}</strong><p>{text}</p></div>
                    <SearchCheck size={17} />
                  </motion.div>
                ))}
                <motion.i
                  className="ethosino-sales-v4-proof-packet"
                  animate={reduceMotion ? undefined : { top: ["4%", "91%"] }}
                  transition={{ duration: 3.8, repeat: Infinity, ease: "linear" }}
                />
              </Reveal>

              <Reveal direction="right" className="ethosino-sales-v4-proof-terminal">
                <div className="ethosino-sales-v4-terminal-head">
                  <div><span /><span /><span /></div>
                  <small>ILLUSTRATIVE VERIFIER</small>
                  <b>EXAMPLE</b>
                </div>
                <pre>{`$ verify-round --example sample_001

mode                 ILLUSTRATIVE
sample game          mines
commitment           HASH MATCH
nonce                SAMPLE INPUT
outcome calculation  REPRODUCED
recorded outcome     MATCH
ruleset              VERSIONED

✓ EXAMPLE VERIFICATION COMPLETE`}</pre>
                <div className="ethosino-sales-v4-terminal-foot">
                  <ShieldCheck size={15} />
                  <span>Sample output shown. Supported game verification is not certification or legal approval.</span>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="ethosino-sales-v4-section ethosino-sales-v4-section--dark" id="ethosino-sales-scale-v4">
          <div className="ethosino-sales-v4-shell">
            <Reveal direction="right" className="ethosino-sales-v4-section-head">
              <span className="ethosino-sales-v4-kicker">AN OPERATOR LAUNCH, IN FIVE DECISIONS</span>
              <h2>Prove the operating model before increasing exposure.</h2>
              <p>
                Our preferred first launch is intentionally limited. Define who can play, test the integrations and controls, observe how the software behaves under real operational conditions, then expand only after the evidence supports it.
              </p>
            </Reveal>

            <div className="ethosino-sales-v4-scale-rail">
              {[
                ["01", "SCOPE", "Agree on the intended audience, permitted jurisdictions and a first-launch feature set."],
                ["02", "PROVISION", "Initialize the operator, activate staff accounts and apply the brand configuration."],
                ["03", "TEST", "Exercise payments, game settlement, access restrictions, withdrawal review and recovery paths."],
                ["04", "MONITOR", "Introduce controlled traffic and examine reliability, support cases and player behavior."],
                ["05", "GROW", "Increase capacity or add content only after operational and commercial milestones are met."]
              ].map(([number, title, text], index) => (
                <motion.article
                  key={title}
                  initial={reduceMotion ? false : { opacity: 0, x: index % 2 === 0 ? -80 : 80 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.22 }}
                  transition={{ duration: 0.6, delay: index * 0.06 }}
                >
                  <span>{number}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </motion.article>
              ))}
              <motion.div
                className="ethosino-sales-v4-scale-signal"
                animate={reduceMotion ? undefined : { left: ["1%", "94%"] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </div>
        </section>


        <section
          className="ethosino-sales-v4-section espc-section"
          id="ethosino-sales-pricing-v4"
        >
          {/* CINEMATIC BACKGROUND */}
          <div className="espc-environment" aria-hidden="true">
            <div className="espc-ambient espc-ambient--left" />
            <div className="espc-ambient espc-ambient--right" />
            <div className="espc-perspective-grid" />
            <div className="espc-horizon" />

            <div className="espc-network">
              {width > 825 && (
                <SalesInfrastructureField
                  primary="#24e7c3"
                  secondary="#27b8ef"
                  accent="#a78bfa"
                  nodeCount={65}
                  connectionDistance={155}
                  pulseCount={5}
                  maxFps={30}
                />
              )}
            </div>
          </div>

          <div className="ethosino-sales-v4-shell espc-shell">

            {/* SECTION HEADING */}
            <motion.div
              className="espc-heading"
              initial={reducePricingMotion ? false : {
                opacity: 0,
                y: 35
              }}
              whileInView={{
                opacity: 1,
                y: 0
              }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.75 }}
            >
              <div className="espc-eyebrow">
                <span className="espc-eyebrow-line" />
                OPERATOR LICENSING OPTIONS
                <span className="espc-eyebrow-index">
                  / 04
                </span>
              </div>

              <h2>
                Start with the right scope.
                <span>Agree on the economics.</span>
              </h2>

              <p>
                Choose a starting point for commercial discussion.
                The cards outline potential implementations and support
                structures—not automatic approval, guaranteed readiness
                or a substitute for a scoped agreement.
              </p>

              <div className="espc-heading-meta">
                <span>
                  <Check size={13} />
                  SEVERAL STARTING POINTS
                </span>

                <span>
                  <Check size={13} />
                  INTEGRATIONS PRICED BY SCOPE
                </span>

                <span>
                  <Check size={13} />
                  TERMS AGREED IN WRITING
                </span>
              </div>
            </motion.div>

            {/* GALLERY HEADER */}
            <div className="espc-gallery-header">
              <div className="espc-gallery-label">
                <Layers3 size={17} />

                <div>
                  <strong>COMPARE LICENSING SCENARIOS</strong>
                  <span>
                    See included scope, fees and launch assumptions
                  </span>
                </div>
              </div>

              <div className="espc-gallery-controls">
                <span className="espc-gallery-counter">
                  {String(activePricingIndex + 1).padStart(2, "0")}
                  <i />
                  {String(pricingCount).padStart(2, "0")}
                </span>

                <button
                  type="button"
                  onClick={() => goToPricing(activePricingIndex - 1)}
                  aria-label="Previous commercial model"
                  disabled={pricingCount <= 1}
                >
                  <ChevronLeft size={19} />
                </button>

                <button
                  type="button"
                  onClick={() => goToPricing(activePricingIndex + 1)}
                  aria-label="Next commercial model"
                  disabled={pricingCount <= 1}
                >
                  <ChevronRight size={19} />
                </button>
              </div>
            </div>

            {/* 3D PRICING STAGE */}
            <div className="espc-stage">

              <div
                className="espc-stage-glow"
                aria-hidden="true"
              />

              {pricingPlans.map((plan, index) => {
                const offset = getPricingPosition(index);
                const isActive = offset === 0;
                const isVisible = width <= 825
                  ? isActive
                  : Math.abs(offset) <= 2;

                if (!isVisible) return null;

                return (
                  <motion.article
                    key={plan.name}
                    className={[
                      "espc-card",
                      isActive ? "is-active" : "",
                      plan.featured ? "is-featured" : ""
                    ].join(" ")}
                    initial={false}
                    animate={
                      width <= 825 || reducePricingMotion
                        ? {
                          x: 0,
                          scale: 1,
                          opacity: 1,
                          rotateY: 0,
                          rotateZ: 0,
                          z: 0
                        }
                        : {
                          x: `${offset * 77}%`,
                          scale: isActive ? 1 : 0.78,
                          opacity: isActive
                            ? 1
                            : Math.abs(offset) === 1
                              ? 0.65
                              : 0.2,
                          rotateY: offset * -19,
                          rotateZ: offset * -2.5,
                          z: isActive ? 110 : -180
                        }
                    }
                    transition={{
                      type: "spring",
                      stiffness: 115,
                      damping: 23,
                      mass: 0.9
                    }}
                    style={{
                      zIndex: 10 - Math.abs(offset),
                      pointerEvents: isActive ? "auto" : "none"
                    }}
                    aria-hidden={!isActive}
                  >
                    <div className="espc-card-shine" />

                    <div className="espc-card-topline">
                      <span>
                        MODEL {String(index + 1).padStart(2, "0")}
                      </span>

                      <span className="espc-card-type">
                        {plan.featured
                          ? "LIMITED PILOT"
                          : index >= 4
                            ? "NEGOTIATED"
                            : "LICENSE OPTION"}
                      </span>
                    </div>

                    <div className="espc-card-heading">
                      <div className="espc-card-icon">
                        {plan.featured
                          ? <Sparkles size={22} />
                          : <Layers3 size={22} />}
                      </div>

                      <h3>{plan.name}</h3>
                      <p>{plan.ideal}</p>
                    </div>

                    <div className="espc-card-pricing">
                      <div>
                        <small>SETUP</small>
                        <strong>{plan.implementation}</strong>
                      </div>

                      <div>
                        <small>RECURRING FEE</small>
                        <strong>{plan.licensing}</strong>
                      </div>
                    </div>

                    <div className="espc-card-details">
                      <div>
                        <span>REVENUE SHARE</span>
                        <strong>{plan.revShare}</strong>
                      </div>

                      <div>
                        <span>EST. DEPLOYMENT</span>
                        <strong>{plan.timeline}</strong>
                      </div>

                      <div>
                        <span>OPERATING PROFILE</span>
                        <strong>{plan.minimumVolume}</strong>
                      </div>
                    </div>

                    <div className="espc-card-features">
                      <span>ILLUSTRATIVE SCOPE</span>

                      <ul>
                        {plan.features.slice(0, 4).map(feature => (
                          <li key={feature}>
                            <Check size={14} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <button
                      type="button"
                      className="espc-card-button"
                      onClick={() => openPricingDetails(plan)}
                    >
                      VIEW OPTION DETAILS
                      <ArrowUpRight size={17} />
                    </button>

                    <div className="espc-card-footer">
                      <ShieldCheck size={13} />
                      NO BINDING OFFER WITHOUT AGREEMENT
                    </div>
                  </motion.article>
                );
              })}
            </div>

            {/* MODEL SELECTOR */}
            <div
              className="espc-model-selector"
              role="tablist"
              aria-label="Commercial models"
            >
              {pricingPlans.map((plan, index) => (
                <button
                  key={plan.name}
                  type="button"
                  role="tab"
                  aria-selected={activePricingIndex === index}
                  className={
                    activePricingIndex === index
                      ? "is-active"
                      : ""
                  }
                  onClick={() => goToPricing(index)}
                >
                  <span>
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  {plan.name}
                </button>
              ))}
            </div>

            {/* COMMERCIAL DISCLAIMER */}
            <div className="espc-disclaimer">
              <span>
                <ShieldCheck size={15} />
                PROPOSALS ARE SCOPED INDIVIDUALLY
              </span>

              <p>
                Listed fees, percentages and dates are indicative only.
                Actual pricing and launch timing require agreed scope,
                integration plans, diligence and legal readiness.
              </p>

              <Link to="/pricing">
                VIEW PRICING FRAMEWORK
                <ArrowRight size={14} />
              </Link>
            </div>

          </div>
        </section>


        <section className="ethosino-sales-v4-section" id="ethosino-sales-boundary-v4">
          <div className="ethosino-sales-v4-shell">
            <Reveal direction="right" className="ethosino-sales-v4-boundary-card">
              <div className="ethosino-sales-v4-boundary-icon"><Scale size={26} /></div>
              <div>
                <span className="ethosino-sales-v4-kicker">LAUNCH RESPONSIBILITIES</span>
                <h2>A software license is not a gaming license.</h2>
                <p>
                  You bring the lawful operating model. Ethosino supplies technology that can be configured for it. Before production, the operator must confirm applicable rules, secure any required approvals and establish suitable payment, identity and player-protection arrangements.
                </p>
              </div>
              <div className="ethosino-sales-v4-boundary-points">
                <div><FileCheck2 size={16} /><span>The operator determines the legal basis for its activities</span></div>
                <div><Globe2 size={16} /><span>Access restrictions are configured to match the approved markets</span></div>
                <div><ShieldCheck size={16} /><span>Technology supports compliance work but cannot replace legal review</span></div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="ethosino-sales-v4-section ethosino-sales-v4-faq-section" id="ethosino-sales-faq-v4">
          <div className="ethosino-sales-v4-shell ethosino-sales-v4-faq-layout">
            <Reveal direction="left" className="ethosino-sales-v4-faq-sticky">
              <span className="ethosino-sales-v4-kicker">BEFORE YOU COMMIT</span>
              <h2>Know exactly what you are evaluating.</h2>
              <p>
                Product fit involves more than a screenshot. These answers clarify configuration, supplied software, operator obligations and the steps between a demonstration and an authorized launch.
              </p>
              <Link to="/book-demo" className="ethosino-sales-v4-button ethosino-sales-v4-button--ghost">
                Discuss Your Requirements <ArrowRight size={15} />
              </Link>
            </Reveal>

            <Reveal direction="right" className="ethosino-sales-v4-faq-list">
              {faqItems.map((item, index) => {
                const isOpen = openFaq === index;

                return (
                  <div className={`ethosino-sales-v4-faq-item ${isOpen ? "is-open" : ""}`} key={item.q}>
                    <button type="button" onClick={() => setOpenFaq(isOpen ? -1 : index)}>
                      <span>0{index + 1}</span>
                      <strong>{item.q}</strong>
                      <motion.i animate={{ rotate: isOpen ? 180 : 0 }}><ChevronDown size={18} /></motion.i>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28 }}
                          className="ethosino-sales-v4-faq-answer"
                        >
                          <p>{item.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </Reveal>
          </div>
        </section>

        <section className="ethosino-sales-v4-final" id="ethosino-sales-final-v4">
          <div className="ethosino-sales-v4-final-grid" />
          <div className="ethosino-sales-v4-shell">
            <Reveal direction="up" className="ethosino-sales-v4-final-card">
              <div className="ethosino-sales-v4-final-signal"><i /> DEMO AVAILABLE</div>
              <span className="ethosino-sales-v4-kicker">YOUR NEXT STEP / AN OPERATOR CONVERSATION</span>
              <h2>Tell us what you want to launch.</h2>
              <p>
                We'll show the relevant product areas, discuss your intended markets and audience, and identify what already exists versus what needs integration or custom work. Leave with a clearer technical scope—not a vague promise to launch overnight.
              </p>
              <div className="ethosino-sales-v4-final-actions">
                <Link to="/book-demo" className="ethosino-sales-v4-button ethosino-sales-v4-button--primary">
                  Schedule an Operator Conversation <ArrowRight size={16} />
                </Link>
                <Link to="/request-documentation" className="ethosino-sales-v4-button ethosino-sales-v4-button--ghost">
                  Request a Technical Overview <FileCheck2 size={16} />
                </Link>
              </div>

              <div className="ethosino-sales-v4-final-trust">
                {["Product demonstration", "Scope review", "Integration planning", "Pilot readiness"].map(item => (
                  <span key={item}><Check size={13} />{item}</span>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
        <SalesFooter previewImage={operatorPreview} />
      </main>
    </Fragment>
  );
};


export default App;
